import {
  type ResourceFragment,
  type ResourcesQuery,
  type ResourcesQueryVariables,
  useResourcesQuery,
} from "@graphql/resources/resources.g";
import { SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";
import { useTableColumnSettings } from "@providers/TableColumnSettingsProvider/useTableColumnSettings";

import {
  ADMIN_APPLICATION_TABLE_COLUMN_SETTINGS,
  APPLICATION_TABLE_COLUMN_LABELS,
  ApplicationTableColumns,
  DEFAULT_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "../UI/ApplicationsTableColumns";
import { AdminApplicationsTableConfig, ApplicationsTableConfig } from "../ApplicationsTableConfig";

import {
  exportTableData,
  batchFetchData,
  buildSortedQueryParams
} from "@utils/table/tableExportUtils";

type Props = {
  tableId: string;
  exportType?: "csv";
  // Filter parameters - same as the table component
  search?: string;
  isAdminView?: boolean;
  isSaasFilter?: "all" | "saas" | "non-saas";
  sorting?: SortingState;
};

export const useApplicationTableExport = ({
  tableId,
  exportType = "csv",
  search,
  isAdminView,
  isSaasFilter,
  sorting,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);
  const { getColumnSettings, getColumnOrder } = useTableColumnSettings({
    tableId,
  });

  const mapDataToExport = useCallback((
    node: ResourceFragment,
    columns: ApplicationTableColumns[]
  ): Record<string, unknown> => {
    const application: Record<string, unknown> = {};

    columns.forEach((column) => {
      const label = APPLICATION_TABLE_COLUMN_LABELS[column];

      switch (column) {
        case ApplicationTableColumns.productName:
          application[label] = node.productName || "";
          break;
        case ApplicationTableColumns.vendorName:
          application[label] = node.vendorName || "";
          break;
        case ApplicationTableColumns.productDomain:
          application[label] = node.productDomainName || "";
          break;
        case ApplicationTableColumns.vendorDomain:
          application[label] = node.vendorDomainName || "";
          break;
        case ApplicationTableColumns.category:
          application[label] = node.category?.name || "";
          break;
        case ApplicationTableColumns.mspVitalityScore:
          application[label] = node.mspVitalityScore || 0;
          break;
        case ApplicationTableColumns.isForConsumers:
          application[label] = node.isForConsumers ? "Yes" : "No";
          break;
        case ApplicationTableColumns.createdAt:
          application[label] = node.createdAt
            ? new Date(node.createdAt).toLocaleDateString()
            : "";
          break;
        case ApplicationTableColumns.updatedAt:
          application[label] = node.updatedAt
            ? new Date(node.updatedAt).toLocaleDateString()
            : "";
          break;
        case ApplicationTableColumns.productDescription:
          application[label] = node.productDescription || "";
          break;
        case ApplicationTableColumns.vendorDescription:
          application[label] = node.vendorDescription || "";
          break;
        case ApplicationTableColumns.isSaas:
          application[label] = node.isSaas ? "Yes" : "No";
          break;
        case ApplicationTableColumns.isVisible:
          application[label] = node.isVisible ? "Yes" : "No";
          break;
        default:
          application[label] = "";
      }
    });

    return application;
  }, []);

  const exportData = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const BATCH_SIZE = 100;

      // Get table configuration based on admin view
      const tableConfig = isAdminView
        ? AdminApplicationsTableConfig
        : ApplicationsTableConfig;

      // Build query parameters with sorting
      const sortParams = buildSortedQueryParams(
        sorting,
        tableConfig.columns
      );

      // Build base query filters
      const buildQueryFilters = (
        currentOffset: number,
      ): ResourcesQueryVariables => {
        const filters: ResourcesQueryVariables = {
          first: BATCH_SIZE,
          offset: currentOffset,
          search: search || undefined,
          isPrimaryResource: true,
          ...sortParams,
        };

        // Apply isSaas filter for admin view
        if (isAdminView && isSaasFilter && isSaasFilter !== "all") {
          filters.isSaas = isSaasFilter === "saas";
        }

        // Apply isVisible filter for non-admin view
        if (!isAdminView) {
          filters.isVisible = true;
        }

        return filters;
      };

      // Fetch all data with sorting applied
      const allApplications = await batchFetchData<
        ResourceFragment,
        ResourcesQuery
      >(
        async (offset) => {
          const queryFilters = buildQueryFilters(offset);
          return queryClient.fetchQuery({
            queryKey: useResourcesQuery.getKey(queryFilters),
            queryFn: useResourcesQuery.fetcher(queryFilters),
          });
        },
        (result) =>
          result.resources?.edges
            ?.map((edge) => edge?.node)
            .filter((node): node is ResourceFragment => Boolean(node)) || [],
        BATCH_SIZE
      );

      const defaultSettings = isAdminView
        ? ADMIN_APPLICATION_TABLE_COLUMN_SETTINGS
        : DEFAULT_APPLICATION_TABLE_COLUMN_SETTINGS;
      const columnSettings = getColumnSettings() ?? defaultSettings;
      const columnOrder = getColumnOrder() as
        | ApplicationTableColumns[]
        | undefined;

      await exportTableData({
        exportType,
        columnSettings,
        columnOrder,
        data: allApplications,
        mapDataToExport,
        filename: `applications-${new Date().toISOString().split("T")[0]}.csv`,
      });
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    exportType,
    getColumnSettings,
    getColumnOrder,
    search,
    sorting,
    isExporting,
    isAdminView,
    isSaasFilter,
    mapDataToExport,
  ]);

  return useMemo(
    () => ({ exportData, isExporting }),
    [exportData, isExporting],
  );
};
